"use client";

import { useState } from "react";
import Image from "next/image";
import { CandlePatternInfo } from "@/lib/candlePatternInfo";

interface KnowledgeCentreProps {
  pattern: CandlePatternInfo | null;
  isLoading?: boolean;
}

export default function KnowledgeCentre({
  pattern,
  isLoading = false,
}: KnowledgeCentreProps) {
  const PatternImage = ({ src, alt }: { src: string; alt: string }) => {
    const [error, setError] = useState(false);

    if (error) {
      return <p style={{ color: "var(--color-text-muted)" }}>Pattern image</p>;
    }

    return (
      <Image
        src={src}
        alt={alt}
        width={360}
        height={200}
        unoptimized
        onError={() => setError(true)}
        style={{
          maxWidth: "100%",
          maxHeight: "200px",
          width: "auto",
          height: "auto",
          objectFit: "contain",
        }}
      />
    );
  };
  if (isLoading) {
    return (
      <div
        style={{
          padding: "2rem",
          background: "var(--color-bg-secondary)",
          borderRadius: "var(--radius)",
          textAlign: "center",
        }}
      >
        <p style={{ color: "var(--color-text-muted)" }}>Loading pattern info...</p>
      </div>
    );
  }

  if (!pattern) {
    return (
      <div
        style={{
          padding: "3rem 2rem",
          background: "rgba(59, 130, 246, 0.1)",
          border: "2px dashed rgba(59, 130, 246, 0.3)",
          borderRadius: "var(--radius)",
          textAlign: "center",
        }}
      >
        <p style={{ color: "var(--color-text-muted)", fontSize: "1rem" }}>
          Select a pattern to view details in the Knowledge Centre
        </p>
      </div>
    );
  }

  const typeColor = pattern.type === "Bullish" ? "#10b981" : "#ef4444";
  const reliabilityColor =
    pattern.reliability === "High"
      ? "#10b981"
      : pattern.reliability === "Medium"
        ? "#f59e0b"
        : "#ef4444";
  const quickRead =
    pattern.type === "Bullish"
      ? "Treat this as a bullish setup and wait for upside confirmation before sizing."
      : pattern.type === "Bearish"
        ? "Treat this as a bearish warning and wait for downside confirmation before acting."
        : "Treat this as a context pattern and let the next candle decide the directional bias.";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      {/* Header */}
      <div>
        <h3
          style={{
            color: "var(--color-text)",
            fontSize: "1.5rem",
            fontWeight: "700",
            marginBottom: "0.5rem",
          }}
        >
          {pattern.displayName}
        </h3>
        <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
          <span
            style={{
              background: typeColor,
              color: "white",
              padding: "0.35rem 0.75rem",
              borderRadius: "0.35rem",
              fontSize: "0.85rem",
              fontWeight: "600",
            }}
          >
            {pattern.type}
          </span>
          <span
            style={{
              background: reliabilityColor,
              color: "white",
              padding: "0.35rem 0.75rem",
              borderRadius: "0.35rem",
              fontSize: "0.85rem",
              fontWeight: "600",
            }}
          >
            {pattern.reliability} Reliability
          </span>
        </div>
      </div>

      <div
        style={{
          padding: "1rem 1.05rem",
          background: "rgba(15, 23, 42, 0.72)",
          border: "1px solid rgba(148, 163, 184, 0.2)",
          borderRadius: "var(--radius)",
        }}
      >
        <div
          style={{
            color: "var(--blue-text)",
            fontSize: "0.9rem",
            fontWeight: 700,
            marginBottom: "0.35rem",
          }}
        >
          Quick Read
        </div>
        <p
          style={{
            color: "rgba(241, 245, 249, 0.82)",
            fontSize: "0.88rem",
            lineHeight: "1.65",
            margin: 0,
          }}
        >
          {quickRead}
        </p>
      </div>

      {/* Pattern Image */}
      <div
        style={{
          background: "rgba(30, 41, 59, 0.8)",
          border: "1px solid rgba(148, 163, 184, 0.3)",
          borderRadius: "var(--radius)",
          padding: "1.5rem",
          textAlign: "center",
          minHeight: "200px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {pattern.image ? (
          <PatternImage key={pattern.image} src={pattern.image} alt={pattern.displayName} />
        ) : (
          <p style={{ color: "var(--color-text-muted)" }}>Pattern image</p>
        )}
      </div>

      {/* Description */}
      <div>
        <h4
          style={{
            color: "var(--blue-text)",
            fontSize: "0.95rem",
            fontWeight: "600",
            marginBottom: "0.5rem",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          What It Is
        </h4>
        <p
          style={{
            color: "rgba(241, 245, 249, 0.8)",
            fontSize: "0.9rem",
            lineHeight: "1.6",
            margin: 0,
          }}
        >
          {pattern.description}
        </p>
      </div>

      {/* Formation */}
      <div>
        <h4
          style={{
            color: "var(--blue-text)",
            fontSize: "0.95rem",
            fontWeight: "600",
            marginBottom: "0.5rem",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          Formation
        </h4>
        <p
          style={{
            color: "rgba(241, 245, 249, 0.8)",
            fontSize: "0.9rem",
            lineHeight: "1.6",
            margin: 0,
          }}
        >
          {pattern.formation}
        </p>
      </div>

      {/* Market Signal */}
      <div>
        <h4
          style={{
            color: "var(--blue-text)",
            fontSize: "0.95rem",
            fontWeight: "600",
            marginBottom: "0.5rem",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          Why It Matters
        </h4>
        <p
          style={{
            color: "rgba(241, 245, 249, 0.8)",
            fontSize: "0.9rem",
            lineHeight: "1.6",
            margin: 0,
          }}
        >
          {pattern.marketSignal}
        </p>
      </div>

      {/* Predictive Power */}
      <div>
        <h4
          style={{
            color: "var(--blue-text)",
            fontSize: "0.95rem",
            fontWeight: "600",
            marginBottom: "0.5rem",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          Confirmation
        </h4>
        <p
          style={{
            color: "rgba(241, 245, 249, 0.8)",
            fontSize: "0.9rem",
            lineHeight: "1.6",
            margin: 0,
          }}
        >
          {pattern.predictivePower}
        </p>
      </div>
    </div>
  );
}
